// Utility to match transaction data against custom keywords/red flags
export async function loadCompanyKeywords(companyId, apiToken) {
  try {
    const response = await fetch(
      `/api/compliance/keywords?companyId=${companyId}`,
      { headers: { Authorization: `Bearer ${apiToken}` } }
    );
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    return data.grouped || {};
  } catch (err) {
    console.error('[Keywords] Error loading:', err);
    return {};
  }
}

// Check if text matches any keyword patterns (including regex)
export function matchKeywords(text, keywords) {
  if (!text || !keywords || keywords.length === 0) return [];

  const text_lower = (text || '').toLowerCase();
  const matches = [];

  for (const kw of keywords) {
    try {
      if (kw.is_regex) {
        // Regex pattern matching
        try {
          const regex = new RegExp(kw.keyword, 'i');
          if (regex.test(text_lower)) {
            matches.push({
              keyword: kw.keyword,
              category: kw.category,
              risk_level: kw.risk_level,
              is_regex: true,
              matched_text: text.match(regex)?.[0] || text
            });
          }
        } catch (e) {
          console.warn(`Invalid regex pattern: ${kw.keyword}`, e);
        }
      } else {
        // Simple substring match (case-insensitive)
        if (text_lower.includes(kw.keyword.toLowerCase())) {
          matches.push({
            keyword: kw.keyword,
            category: kw.category,
            risk_level: kw.risk_level,
            is_regex: false,
            matched_text: kw.keyword
          });
        }
      }
    } catch (err) {
      console.error(`Error matching keyword: ${kw.keyword}`, err);
    }
  }

  return matches;
}

// Analyze transaction for red flags based on custom keywords
export function analyzeTransactionForRedFlags(transaction, companyKeywords) {
  if (!transaction || !companyKeywords) return { redFlags: [], riskIncrease: 0 };

  const redFlags = [];
  let riskIncrease = 0;

  // Check transaction description
  if (transaction.description) {
    const descMatches = matchKeywords(transaction.description, companyKeywords['risk_flag'] || []);
    redFlags.push(...descMatches.map(m => ({ ...m, source: 'description' })));
    riskIncrease += descMatches.reduce((sum, m) => {
      const riskMap = { critical: 30, high: 20, medium: 10, low: 5 };
      return sum + (riskMap[m.risk_level] || 0);
    }, 0);
  }

  // Check counterparty/entity name
  if (transaction.entity || transaction.counterparty) {
    const entity = transaction.entity || transaction.counterparty;
    const entityMatches = matchKeywords(entity, companyKeywords['risk_flag'] || []);
    redFlags.push(...entityMatches.map(m => ({ ...m, source: 'entity' })));
    riskIncrease += entityMatches.reduce((sum, m) => {
      const riskMap = { critical: 25, high: 15, medium: 8, low: 3 };
      return sum + (riskMap[m.risk_level] || 0);
    }, 0);
  }

  // Check for positive signals (risk decrease)
  if (transaction.description) {
    const positiveMatches = matchKeywords(transaction.description, companyKeywords['positive'] || []);
    redFlags.push(...positiveMatches.map(m => ({ ...m, source: 'description', isPositive: true })));
    riskIncrease -= positiveMatches.reduce((sum, m) => {
      const riskMap = { critical: 10, high: 5, medium: 2, low: 1 };
      return sum + (riskMap[m.risk_level] || 0);
    }, 0);
  }

  // Check for escalation triggers
  if (transaction.description) {
    const escalationMatches = matchKeywords(transaction.description, companyKeywords['escalation'] || []);
    if (escalationMatches.length > 0) {
      redFlags.push(...escalationMatches.map(m => ({ ...m, source: 'description', requiresEscalation: true })));
      riskIncrease += escalationMatches.length * 5;
    }
  }

  return {
    redFlags,
    riskIncrease: Math.max(-30, Math.min(30, riskIncrease)), // Cap between -30 and +30
    hasRedFlags: redFlags.some(f => !f.isPositive),
    hasEscalation: redFlags.some(f => f.requiresEscalation),
    summary: redFlags.length > 0 ? `Found ${redFlags.filter(f => !f.isPositive).length} red flag(s)` : 'No red flags detected'
  };
}

// Score risk increase based on risk level
export function riskLevelToScore(level) {
  const scores = { critical: 30, high: 20, medium: 10, low: 5 };
  return scores[level] || 0;
}
